import { convertConstructorColumnTitles } from "../../../../utils";
import { useLocation } from "react-router-dom";

export type SearchCriterion = {
	searchColumn: {
		Header: string | React.ReactNode;
		headerText: string;
		type?: string;
		id?: string;
	};
	submittedSearchText: string | string[];
};

type AdditionalData = {
	whereClause?: string;
};

// 1) Define a ColumnMapping type that matches your real data:
type ColumnMapping = {
	// some mappings use `field`, others use `name`
	field?: string;
	name?: string;
	slug: string;
	// any extra props you have (e.g. settings)
	[key: string]: any;
};

type UseConstructUrlOptions = {
	slug: string;
	searchCriteria: SearchCriterion[];
	pageSize: number;
	sortColumn: Record<string, string>;
	// 2) Use the new ColumnMapping[] type here
	columnMappings: ColumnMapping[];
	page: number;
	pageMeta?: any;
};

export default function useConstructUrl({
	slug,
	searchCriteria,
	pageSize,
	sortColumn,
	page,
	columnMappings,
	pageMeta,
}: UseConstructUrlOptions): string {
	// 3) Normalize your “field” identifier to either .field or .name
	const validFields = new Set(
		columnMappings.map((m) => m.field ?? m.name).filter(Boolean)
	);

	const location = useLocation();
	const params = new URLSearchParams(location.search);

	const validCriteria = searchCriteria.filter(
		({ searchColumn }) =>
			!!searchColumn.id && validFields.has(searchColumn.id)
	);

	const converted = convertConstructorColumnTitles(pageMeta, columnMappings);

	const urlParts: string[] = [];

	// checks the params to see if there are any filters in the url
	// currently, we only have the case of 'approvals'
	// but this is dynamic enough to handle any future filters added to the url
	params.forEach((value, key) => {
		if (key === "filters") {
			urlParts.push(`${key}=${value}`);
		}
	});

	validCriteria.forEach(({ searchColumn, submittedSearchText }) => {
		// a) headerText → converted mapping
		let mapping = converted.find((item) => {
			const label = item.labelSingular.replace(/\W+/g, "").toLowerCase();
			const hdr = searchColumn.headerText
				.replace(/\W+/g, "")
				.toLowerCase();
			return label.includes(hdr);
		});

		// b) fallback to original mapping by field or name
		if (!mapping) {
			const orig = columnMappings.find(
				(m) => (m.field ?? m.name) === searchColumn.id
			);
			if (orig) {
				mapping = {
					slug: orig.slug,
					// labelSingular only used for debug
					labelSingular: orig.name ?? orig.field!,
				};
			}
		}

		// c) if still missing, drop it
		if (!mapping) {
			console.warn(
				`Dropping filter "${searchColumn.headerText}"—no mapping found.`
			);
			return;
		}

		const values = Array.isArray(submittedSearchText)
			? submittedSearchText
			: [submittedSearchText];
		const joined = values.map(customEncodeURIComponent).join(":");
		urlParts.push(
			`${slug}_${customEncodeURIComponent(mapping.slug)}=${joined}`
		);
	});

	urlParts.push(`${slug}_recordsPerPage=${pageSize}`);
	urlParts.push(`${slug}_page=${page}`);

	new URLSearchParams(window.location.search).forEach((value, key) => {
		if (
			key.startsWith(`${slug}_`) &&
			!key.startsWith(`${slug}_sort`) &&
			!key.endsWith("_recordsPerPage") &&
			!key.endsWith("_page")
		) {
			urlParts.push(`${key}=${value}`);
		}
	});

	// sortColumn
	let sortIndex = 0;
	Object.entries(sortColumn).forEach(([fieldKey, direction]) => {
		if (!direction || fieldKey === "col") {
			return;
		}
		// find the matching slug for this field/name
		const mappingItem = columnMappings.find(
			(m) => (m.field ?? m.name) === fieldKey
		);
		const sortSlug = mappingItem ? mappingItem.slug : fieldKey;
		// append exactly "sales-orders_sort[0]=+status", "…[1]=-name", etc.
		urlParts.push(
			`${slug}_sort[${sortIndex++}]=${customEncodeURIComponent(
				direction
			)}`
		);
	});

	// assemble final URL
	let baseUrl = `?${urlParts.join("&")}`;
	const orders = localStorage.getItem("orders");
	if (orders) baseUrl += `&Orders=${orders}`;
	const uuid = localStorage.getItem("uuid");
	if (uuid) baseUrl += `&uuid=${uuid}`;

	return baseUrl;
}

function customEncodeURIComponent(component: string): string {
	return encodeURIComponent(component).replace(/%2B/g, "+");
}
